import { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Sprout, LayoutDashboard, Calendar, Clock, LogOut, 
  Euro, ChevronRight, CheckCircle2, XCircle, CalendarDays, FileText, Download,
  Star, MessageSquare, Send, Phone, Mail, MapPin, ExternalLink, Menu, X,
  CalendarPlus, ChevronLeft, Loader2, Info, Briefcase, Repeat, Video, ArrowLeft, Search, Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Textarea } from '../../components/ui/textarea';
import { clientApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../../components/NotificationBell';
import { ClientDashboardGuideTour } from '../../components/GuideTour';
import { getAssetUrl } from '../../lib/assetUrl';
import NewVersionToast from '../../components/NewVersionToast';
import { getSlotTypeInfo, slotTypeBadgeClasses, slotTypeEmoji } from '../../lib/slotTypeLabel';
import NotificationsPage from '../NotificationsPage';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';

// My contact details editable card — visible on the client Dashboard.
// Uses AddressAutocomplete (BAN) for the address field and lists a completion
// progress bar so the client is nudged to fill their profile once.
const MyContactCard = ({ profile, onProfileUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [form, setForm] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || (profile?.name?.split(' ').slice(1).join(' ') || ''),
    mobile_phone: profile?.mobile_phone || profile?.phone || '',
    address: profile?.address || '',
    birthdate: profile?.birthdate || '',
  });

  useEffect(() => {
    // Best-effort split when only `name` is present
    let fn = profile?.first_name || '';
    let ln = profile?.last_name || '';
    if (!fn && !ln && profile?.name) {
      const parts = profile.name.split(' ');
      fn = parts[0] || '';
      ln = parts.slice(1).join(' ') || '';
    }
    setForm({
      first_name: fn,
      last_name: ln,
      mobile_phone: profile?.mobile_phone || profile?.phone || '',
      address: profile?.address || '',
      birthdate: profile?.birthdate || '',
    });
  }, [profile?.id, profile?.name, profile?.address, profile?.birthdate]);

  const completion = profile?.profile_completion_percentage || 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await clientApi.updateProfile({
        first_name: form.first_name,
        last_name: form.last_name,
        mobile_phone: form.mobile_phone,
        phone: form.mobile_phone,
        address: form.address,
        birthdate: form.birthdate,
      });
      toast.success('Vos coordonnées ont été mises à jour');
      onProfileUpdated?.({
        ...profile,
        ...form,
        name: `${form.first_name} ${form.last_name}`.trim() || profile?.name,
        phone: form.mobile_phone,
        profile_completion_percentage: res.data?.profile_completion_percentage ?? completion,
      });
      setIsEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de mettre à jour vos coordonnées');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="client-my-contact-card">
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-4">
        <div className="flex items-start gap-4">
          {/* Avatar (uploadable) */}
          <div className="relative flex-shrink-0">
            <div className="w-16 h-16 rounded-2xl overflow-hidden bg-primary/10 flex items-center justify-center text-primary font-bold text-lg border">
              {profile?.avatar_url ? (
                <img src={getAssetUrl(profile.avatar_url)} alt="Avatar" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <span>{(profile?.first_name || profile?.name || '?').charAt(0).toUpperCase()}</span>
              )}
            </div>
            <label className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm cursor-pointer hover:scale-105 transition-transform" title="Changer la photo" data-testid="client-avatar-upload-label">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={avatarUploading}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setAvatarUploading(true);
                  try {
                    const res = await clientApi.uploadAvatar(file);
                    toast.success('Photo mise à jour');
                    onProfileUpdated?.({ ...profile, avatar_url: res.data.avatar_url });
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Upload impossible');
                  } finally {
                    setAvatarUploading(false);
                    e.target.value = '';
                  }
                }}
                data-testid="client-avatar-file-input"
              />
              {avatarUploading ? (
                <span className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
            </label>
          </div>
          <div>
            <CardTitle className="font-heading text-lg">Mes coordonnées</CardTitle>
            {/* Profile completion gauge */}
            <div className="mt-2 min-w-[220px]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Profil complété
                </span>
                <span
                  className={`text-xs font-bold ${completion >= 100 ? 'text-emerald-600' : completion >= 60 ? 'text-blue-600' : 'text-amber-600'}`}
                  data-testid="profile-completion-percent"
                >
                  {completion}%
                </span>
              </div>
              <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${completion >= 100 ? 'bg-emerald-500' : completion >= 60 ? 'bg-blue-500' : 'bg-amber-500'}`}
                  style={{ width: `${completion}%` }}
                />
              </div>
            </div>
          </div>
        </div>
        {isEditing ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={saving} data-testid="client-contact-cancel">
              Annuler
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="client-contact-save">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="client-contact-edit">
            Modifier
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prénom</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                data-testid="client-first-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                data-testid="client-last-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Téléphone mobile</Label>
              <Input
                type="tel"
                value={form.mobile_phone}
                onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })}
                placeholder="06 12 34 56 78"
                data-testid="client-mobile-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Date de naissance</Label>
              <Input
                type="date"
                value={form.birthdate}
                onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                data-testid="client-birthdate-input"
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Adresse postale principale</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                placeholder="Commencez à taper votre adresse…"
                data-testid="client-contact-address-autocomplete"
              />
              <p className="text-xs text-muted-foreground">
                Sera pré-remplie dans toutes vos futures réservations.
              </p>
            </div>
            <div className="md:col-span-2 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Votre email <span className="font-medium">{profile?.email}</span> ne peut pas être modifié depuis cet espace. Contactez votre prestataire si besoin.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ContactField icon={Sprout} label="Nom complet" value={profile?.name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || '—'} tone="primary" />
            <ContactField icon={Phone} label="Mobile" value={profile?.mobile_phone || profile?.phone} tone="green" href={profile?.mobile_phone || profile?.phone ? `tel:${profile.mobile_phone || profile.phone}` : null} />
            <ContactField icon={Mail} label="Email" value={profile?.email} tone="blue" />
            <ContactField icon={Calendar} label="Date de naissance" value={profile?.birthdate ? new Date(profile.birthdate).toLocaleDateString('fr-FR') : null} tone="purple" />
            <div className="md:col-span-2">
              <ContactField
                icon={MapPin}
                label="Adresse postale"
                value={profile?.address}
                emptyLabel="Adresse non renseignée — cliquez sur Modifier"
                tone={profile?.address ? 'purple' : 'amber'}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ContactField = ({ icon: Icon, label, value, tone = 'primary', href = null, emptyLabel = '—' }) => {
  const toneClasses = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
  }[tone] || 'bg-primary/10 text-primary';
  const Container = href ? 'a' : 'div';
  const containerProps = href ? { href } : {};
  return (
    <Container {...containerProps} className={`flex items-center gap-3 ${href ? 'hover:bg-muted/50 p-2 -m-2 rounded-lg' : ''}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneClasses}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className={`font-medium truncate ${!value ? 'italic' : ''}`}>{value || emptyLabel}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </Container>
  );
};

// ------- Client address book -------
const AddressBookCard = () => {
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draft, setDraft] = useState({ label: '', full_address: '' });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await clientApi.listAddresses();
      setAddresses(res.data || []);
    } catch (e) {
      toast.error('Impossible de charger le carnet d\'adresses');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft.full_address.trim()) {
      toast.error('Adresse requise');
      return;
    }
    try {
      if (editingId) {
        await clientApi.updateAddress(editingId, draft);
        toast.success('Adresse mise à jour');
      } else {
        await clientApi.addAddress(draft);
        toast.success('Adresse ajoutée à votre carnet');
      }
      setDraft({ label: '', full_address: '' });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enregistrement impossible');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette adresse ?')) return;
    try {
      await clientApi.deleteAddress(id);
      load();
    } catch (e) {
      toast.error('Suppression impossible');
    }
  };

  const setDefault = async (a) => {
    try {
      await clientApi.updateAddress(a.id, { ...a, is_default: true });
      load();
    } catch (e) {
      toast.error('Impossible de définir par défaut');
    }
  };

  return (
    <Card data-testid="client-address-book-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="font-heading text-lg">Carnet d'adresses</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Enregistrez plusieurs adresses (domicile, résidence secondaire…) pour aller plus vite lors des réservations.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : addresses.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Aucune adresse enregistrée pour le moment.</p>
        ) : (
          <div className="space-y-2" data-testid="client-address-list">
            {addresses.map((a) => (
              <div key={a.id} className={`flex items-start gap-3 p-3 rounded-lg border ${a.is_default ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{a.label}</p>
                    {a.is_default && <Badge className="bg-primary text-primary-foreground text-[10px]">Par défaut</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{a.full_address}</p>
                </div>
                <div className="flex flex-col gap-1">
                  {!a.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => setDefault(a)} data-testid={`address-set-default-${a.id}`}>
                      Définir par défaut
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { setDraft({ label: a.label, full_address: a.full_address, is_default: a.is_default }); setEditingId(a.id); }} data-testid={`address-edit-${a.id}`}>
                    Modifier
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(a.id)} data-testid={`address-delete-${a.id}`}>
                    Supprimer
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add / edit form */}
        <div className="border-t pt-4 space-y-2">
          <p className="text-sm font-medium">{editingId ? 'Modifier une adresse' : 'Ajouter une adresse'}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="md:col-span-1">
              <Input
                placeholder="Libellé (Domicile, Résidence…)"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                data-testid="address-label-input"
              />
            </div>
            <div className="md:col-span-2">
              <AddressAutocomplete
                value={draft.full_address}
                onChange={(v) => setDraft({ ...draft, full_address: v })}
                onSelect={(sel) => setDraft(prev => ({ ...prev, full_address: sel.label, city: sel.city, postcode: sel.postcode }))}
                placeholder="Commencez à taper l'adresse…"
                data-testid="address-full-address-autocomplete"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            {editingId && (
              <Button size="sm" variant="outline" onClick={() => { setDraft({ label: '', full_address: '' }); setEditingId(null); }}>
                Annuler
              </Button>
            )}
            <Button size="sm" onClick={save} data-testid="address-save-btn">
              {editingId ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ------- Onboarding modal (opens if profile completion < 60%) -------
const ProfileOnboardingModal = ({ profile, onProfileUpdated }) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem('resawork-client-onboarding-dismissed') === '1';
    } catch { return false; }
  });

  useEffect(() => {
    if (!profile) return;
    if (dismissed) return;
    if ((profile.profile_completion_percentage || 0) < 60) {
      setOpen(true);
    }
  }, [profile?.profile_completion_percentage, dismissed]);

  if (!profile) return null;

  const remember = () => {
    try { localStorage.setItem('resawork-client-onboarding-dismissed', '1'); } catch {}
    setDismissed(true);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) remember(); }}>
      <DialogContent className="max-w-lg" data-testid="client-onboarding-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Bienvenue 👋</DialogTitle>
          <DialogDescription>
            Complétez votre profil en 30 secondes pour ne plus jamais avoir à
            re-saisir vos coordonnées lors d'une réservation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <MyContactCard profile={profile} onProfileUpdated={(p) => { onProfileUpdated?.(p); if ((p.profile_completion_percentage || 0) >= 60) remember(); }} />
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" onClick={remember} data-testid="onboarding-dismiss-btn">
            Plus tard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Stats Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, variant = 'default' }) => (
  <Card className="card-hover">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-heading font-semibold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          variant === 'success' ? 'bg-green-100' : variant === 'warning' ? 'bg-amber-100' : 'bg-primary/10'
        }`}>
          <Icon className={`w-6 h-6 ${
            variant === 'success' ? 'text-green-600' : variant === 'warning' ? 'text-amber-600' : 'text-primary'
          }`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Dashboard Overview
function DashboardOverview() {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subsRes, bookingsRes, profileRes] = await Promise.all([
          clientApi.getSubscriptions(),
          clientApi.getBookings(),
          clientApi.getProfile()
        ]);
        setSubscriptions(subsRes.data);
        setBookings(bookingsRes.data);
        setProfile(profileRes.data);
        // Company info is now in profile.company
        if (profileRes.data?.company) {
          setCompanySettings(profileRes.data.company);
        }
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeSub = subscriptions.find(s => s.is_active);
  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' && new Date(b.date) >= new Date());
  const completedBookings = bookings.filter(b => b.status === 'completed');
  const company = profile?.company || companySettings;

  if (isLoading) {
    return <div className="animate-pulse space-y-6">
      <div className="h-8 bg-muted rounded w-1/3" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-xl" />)}
      </div>
    </div>;
  }

  return (
    <div className="space-y-8 stagger-children">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Bienvenue, {user?.name}</h1>
        <p className="text-muted-foreground mt-1">Gérez vos réservations et prestations</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard 
          title="Réservations à venir" 
          value={upcomingBookings.length}
          subtitle="Interventions planifiées"
          icon={Calendar}
          variant={upcomingBookings.length > 0 ? 'warning' : 'default'}
        />
        <StatCard 
          title="Interventions réalisées" 
          value={completedBookings.length}
          subtitle="Historique complet"
          icon={FileText}
        />
        <StatCard 
          title="Statut" 
          value={activeSub ? 'Client actif' : 'Ponctuel'}
          subtitle={activeSub ? 'Abonnement en cours' : 'Réservations à la demande'}
          icon={CheckCircle2}
          variant={activeSub ? 'success' : 'default'}
        />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NavLink to="/client/bookings">
          <Card className="card-hover cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Réserver un créneau</p>
                <p className="text-sm text-muted-foreground">Planifier une nouvelle intervention</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
        </NavLink>
        
        {company?.google_review_link ? (
          <a 
            href={`${process.env.REACT_APP_BACKEND_URL}/api/track/review/${company.id}?client_id=${profile?.id || ''}&email=${profile?.email || ''}`} 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="google-review-link"
          >
            <Card className="card-hover cursor-pointer h-full border-yellow-200 bg-yellow-50">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                  <Star className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-yellow-800">Déposer un avis</p>
                  <p className="text-sm text-yellow-700">Donnez votre avis sur Google</p>
                </div>
                <ChevronRight className="w-5 h-5 text-yellow-600" />
              </CardContent>
            </Card>
          </a>
        ) : (
          <Card className="h-full opacity-60">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                <Star className="w-6 h-6 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Déposer un avis</p>
                <p className="text-sm text-muted-foreground">Lien Google non configuré</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upcoming bookings */}
      {upcomingBookings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              Prochaines interventions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcomingBookings.slice(0, 3).map((booking) => (
              <div key={booking.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="font-medium">
                    {new Date(booking.date).toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {(() => {
                      const info = getSlotTypeInfo(booking);
                      return `${slotTypeEmoji[info.kind]} ${info.label} · ${info.timeRange || `${info.hours}h`}`;
                    })()}
                    {booking.remaining_charge && ` • ${booking.remaining_charge.toFixed(2)}€`}
                  </p>
                </div>
                <Badge className="status-success">Confirmé</Badge>
              </div>
            ))}
            {upcomingBookings.length > 3 && (
              <NavLink to="/client/bookings">
                <Button variant="outline" className="w-full rounded-full">
                  Voir toutes les réservations ({upcomingBookings.length})
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </NavLink>
            )}
          </CardContent>
        </Card>
      )}

      {/* My contact details (editable) */}
      {profile && (
        <>
          <MyContactCard profile={profile} onProfileUpdated={setProfile} />
          <AddressBookCard />
          <ProfileOnboardingModal profile={profile} onProfileUpdated={setProfile} />
        </>
      )}

      {/* Company contact info */}
      {company && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Coordonnées de l'entreprise</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sprout className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{company.name}</p>
                  <p className="text-sm text-muted-foreground">Entreprise</p>
                </div>
              </div>
              {company.phone && (
                <a href={`tel:${company.phone}`} className="flex items-center gap-3 hover:bg-muted/50 p-2 -m-2 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{company.phone}</p>
                    <p className="text-sm text-muted-foreground">Téléphone</p>
                  </div>
                </a>
              )}
              {company.email && (
                <a href={`mailto:${company.email}`} className="flex items-center gap-3 hover:bg-muted/50 p-2 -m-2 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium truncate">{company.email}</p>
                    <p className="text-sm text-muted-foreground">Email</p>
                  </div>
                </a>
              )}
              {company.address && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">{company.address}</p>
                    <p className="text-sm text-muted-foreground">Adresse</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Bookings Management
function BookingsPage() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [profile, setProfile] = useState(null);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]); // Multiple dates
  const [selectedSlotType, setSelectedSlotType] = useState('half_day');

  const fetchData = async () => {
    try {
      const [subsRes, bookingsRes, availRes, profileRes, widgetsRes] = await Promise.all([
        clientApi.getSubscriptions(),
        clientApi.getBookings(),
        clientApi.getAvailability(),
        clientApi.getProfile(),
        clientApi.getAvailableWidgets().catch(() => ({ data: [] }))
      ]);
      setSubscriptions(subsRes.data);
      setBookings(bookingsRes.data);
      setAvailability(availRes.data);
      setProfile(profileRes.data);
      setAvailableWidgets(widgetsRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const activeSub = subscriptions.find(s => s.is_active);
  const company = profile?.company;
  const availableDates = availability.filter(a => !a.is_booked).map(a => new Date(a.date));

  // Calculate prices
  const pricePerHour = activeSub?.price_per_hour || 35;
  const hoursPerSlot = selectedSlotType === 'half_day' ? 4 : 7;
  const pricePerSlot = hoursPerSlot * pricePerHour;
  const totalPrice = pricePerSlot * selectedDates.length;
  const taxCredit = totalPrice * 0.5; // 50% crédit d'impôts
  const remainingCharge = totalPrice - taxCredit; // Montant restant à charge

  // Toggle date selection
  const toggleDateSelection = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDates(prev => {
      const exists = prev.find(d => d.toISOString().split('T')[0] === dateStr);
      if (exists) {
        return prev.filter(d => d.toISOString().split('T')[0] !== dateStr);
      } else {
        return [...prev, date];
      }
    });
  };

  const isDateSelected = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return selectedDates.some(d => d.toISOString().split('T')[0] === dateStr);
  };

  const handleBook = async () => {
    if (selectedDates.length === 0 || !activeSub) {
      toast.error('Veuillez sélectionner au moins une date');
      return;
    }

    try {
      // Create one booking per selected date
      for (const date of selectedDates) {
        await clientApi.createBooking({
          subscription_id: activeSub.id,
          date: date.toISOString().split('T')[0],
          slot_type: selectedSlotType,
          price: pricePerSlot,
          tax_credit: pricePerSlot * 0.5,
          remaining_charge: pricePerSlot * 0.5
        });
      }
      toast.success(`${selectedDates.length} réservation(s) confirmée(s)! Vous recevrez une facture par prestation.`);
      setIsDialogOpen(false);
      setSelectedDates([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    }
  };

  const handleCancel = async (id) => {
    try {
      await clientApi.cancelBooking(id);
      toast.success('Réservation annulée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const isDateAvailable = (date) => {
    return availableDates.some(d => 
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  };

  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [chooserMode, setChooserMode] = useState('list'); // 'list' or 'subscription'
  const [chooserSearch, setChooserSearch] = useState('');

  const openChooser = () => {
    setChooserMode('list');
    setChooserSearch('');
    setIsChooserOpen(true);
  };

  // Filter widgets when user types in the search box
  const filteredWidgets = (() => {
    const q = chooserSearch.trim().toLowerCase();
    if (!q) return availableWidgets;
    return availableWidgets.filter((w) => {
      const haystack = `${w.name || ''} ${w.description || ''} ${w.widget_type || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  })();
  // Show the search input only when the list is large enough to benefit from it
  const showChooserSearch = availableWidgets.length >= 6;

  const typeMeta = (widgetType) => ({
    punctual: { icon: Calendar, label: 'Intervention ponctuelle', borderColor: 'border-emerald-200', bgColor: 'from-emerald-500/10 to-emerald-500/5', iconColor: 'text-emerald-600' },
    recurring: { icon: Repeat, label: 'Abonnement / Récurrent', borderColor: 'border-blue-200', bgColor: 'from-blue-500/10 to-blue-500/5', iconColor: 'text-blue-600' },
    appointment: { icon: Video, label: 'Rendez-vous', borderColor: 'border-purple-200', bgColor: 'from-purple-500/10 to-purple-500/5', iconColor: 'text-purple-600' },
  }[widgetType] || { icon: Briefcase, label: 'Prestation', borderColor: 'border-slate-200', bgColor: 'from-slate-500/10 to-slate-500/5', iconColor: 'text-slate-600' });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Mes Réservations</h1>
          <p className="text-muted-foreground mt-1">Gérez vos interventions</p>
        </div>
        
        {(activeSub || availableWidgets.length > 0) && (
          <Button
            className="rounded-full"
            data-testid="new-booking-btn"
            onClick={openChooser}
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            Nouvelle demande
          </Button>
        )}
      </div>

      {/* Unified chooser dialog: pick widget type OR subscription slot */}
      <Dialog
        open={isChooserOpen}
        onOpenChange={(open) => {
          setIsChooserOpen(open);
          if (!open) {
            setChooserMode('list');
            setSelectedDates([]);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          {chooserMode === 'list' && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Quelle demande souhaitez-vous effectuer&nbsp;?</DialogTitle>
                <DialogDescription>
                  Choisissez le type de prestation auprès de {company?.name || 'votre prestataire'}
                </DialogDescription>
              </DialogHeader>
              {showChooserSearch && (
                <div className="mt-3 relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="search"
                    autoFocus
                    value={chooserSearch}
                    onChange={(e) => setChooserSearch(e.target.value)}
                    placeholder="Rechercher un widget par nom, description…"
                    data-testid="chooser-search-input"
                    className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60"
                  />
                  {chooserSearch && (
                    <button
                      type="button"
                      onClick={() => setChooserSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      aria-label="Effacer la recherche"
                      data-testid="chooser-search-clear"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {/* Subscription slot booking — only if active subscription and not filtered out */}
                {activeSub && !chooserSearch && (
                  <button
                    type="button"
                    data-testid="chooser-subscription-card"
                    onClick={() => setChooserMode('subscription')}
                    className="text-left p-4 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-500/10 to-amber-500/5 hover:shadow-md transition-all hover:-translate-y-0.5 group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-11 h-11 rounded-xl bg-white/80 flex items-center justify-center text-amber-600">
                        <CalendarDays className="w-5 h-5" />
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition" />
                    </div>
                    <p className="font-semibold text-slate-900 leading-tight">Mon abonnement</p>
                    <p className="text-xs text-slate-500 mt-1">Réserver un créneau d'abonnement</p>
                    <p className="text-xs text-slate-600 mt-2 line-clamp-2">
                      Posez un ou plusieurs créneaux (demi-journée / journée) sur votre abonnement actif.
                    </p>
                  </button>
                )}
                {/* All widgets exposed by the company (filtered by search) */}
                {filteredWidgets.map((w) => {
                  const meta = typeMeta(w.widget_type);
                  const Icon = meta.icon;
                  return (
                    <button
                      key={w.id}
                      type="button"
                      data-testid={`chooser-widget-${w.id}`}
                      onClick={() => {
                        setIsChooserOpen(false);
                        navigate(`/client/reserve/${w.id}`);
                      }}
                      className={`text-left p-4 rounded-2xl border ${meta.borderColor} bg-gradient-to-br ${meta.bgColor} hover:shadow-md transition-all hover:-translate-y-0.5 group`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-11 h-11 rounded-xl bg-white/80 flex items-center justify-center ${meta.iconColor}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition" />
                      </div>
                      <p className="font-semibold text-slate-900 leading-tight">{w.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{meta.label}</p>
                      {w.description && (
                        <p className="text-xs text-slate-600 mt-2 line-clamp-2">{w.description}</p>
                      )}
                    </button>
                  );
                })}
              </div>
              {chooserSearch && filteredWidgets.length === 0 && (
                <div className="text-center py-8 text-sm text-slate-500" data-testid="chooser-no-results">
                  Aucun widget ne correspond à «&nbsp;<span className="font-medium text-slate-700">{chooserSearch}</span>&nbsp;».
                </div>
              )}
            </>
          )}

          {chooserMode === 'subscription' && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChooserMode('list')}
                    className="-ml-2 h-8"
                    data-testid="chooser-back-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Retour
                  </Button>
                </div>
                <DialogTitle className="font-heading">Réserver des créneaux d'abonnement</DialogTitle>
                <DialogDescription>
                  Sélectionnez un ou plusieurs créneaux (1 facture par créneau)
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Type de créneau</label>
                  <Select value={selectedSlotType} onValueChange={setSelectedSlotType}>
                    <SelectTrigger data-testid="slot-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="half_day">Demi-journée (8h-12h) - 4h</SelectItem>
                      <SelectItem value="full_day">Journée complète (8h-16h) - 7h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Sélectionnez vos dates 
                    {selectedDates.length > 0 && (
                      <Badge className="ml-2 bg-primary">{selectedDates.length} sélectionnée(s)</Badge>
                    )}
                  </label>
                  <div className="flex justify-center">
                    <CalendarComponent
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={(dates) => setSelectedDates(dates || [])}
                      disabled={(date) => date < new Date() || !isDateAvailable(date)}
                      modifiers={{
                        available: availableDates
                      }}
                      modifiersStyles={{
                        available: { backgroundColor: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' }
                      }}
                      className="rounded-xl border"
                    />
                  </div>
                  {availableDates.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center">
                      Aucune disponibilité. Contactez votre entreprise.
                    </p>
                  )}
                </div>
                
                {selectedDates.length > 0 && (
                  <div className="p-4 bg-muted rounded-xl space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Dates sélectionnées ({selectedDates.length})</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedDates.sort((a, b) => a - b).map((date, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground">Prix total</p>
                        <p className="font-medium line-through text-muted-foreground">{totalPrice.toFixed(2)}€</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Crédit d'impôts (-50%)</p>
                        <p className="font-medium text-green-600">-{taxCredit.toFixed(2)}€</p>
                      </div>
                    </div>
                    
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                      <p className="text-xs text-muted-foreground">Restant à votre charge</p>
                      <p className="text-2xl font-bold text-primary">{remainingCharge.toFixed(2)}€</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        soit {(remainingCharge / selectedDates.length).toFixed(2)}€ par créneau
                      </p>
                    </div>
                  </div>
                )}

                {/* Billing info */}
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <Euro className="w-4 h-4 inline mr-1" />
                    Vous recevrez <strong>1 facture par créneau</strong> après chaque prestation.
                    Le crédit d'impôts de 50% est déduit automatiquement (avance immédiate URSSAF).
                  </p>
                </div>
                
                <Button 
                  className="w-full rounded-full" 
                  data-testid="confirm-booking-btn"
                  onClick={async () => {
                    await handleBook();
                    setIsChooserOpen(false);
                    setChooserMode('list');
                  }}
                  disabled={selectedDates.length === 0}
                >
                  Confirmer {selectedDates.length > 0 ? `${selectedDates.length} réservation(s)` : 'la réservation'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Available widgets — cards for each booking type exposed by the company */}
      {availableWidgets.length > 0 && (
        <Card data-testid="available-widgets-section">
          <CardHeader>
            <CardTitle className="text-xl font-heading">Réserver une prestation</CardTitle>
            <CardDescription>
              Choisissez le type de demande à effectuer auprès de {company?.name || 'votre prestataire'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableWidgets.map((w) => {
                const typeMeta = {
                  punctual: { icon: Calendar, label: 'Intervention ponctuelle', color: 'from-emerald-500/10 to-emerald-500/5', iconColor: 'text-emerald-600', borderColor: 'border-emerald-200' },
                  recurring: { icon: Repeat, label: 'Abonnement / Récurrent', color: 'from-blue-500/10 to-blue-500/5', iconColor: 'text-blue-600', borderColor: 'border-blue-200' },
                  appointment: { icon: Video, label: 'Rendez-vous', color: 'from-purple-500/10 to-purple-500/5', iconColor: 'text-purple-600', borderColor: 'border-purple-200' },
                }[w.widget_type] || { icon: Briefcase, label: 'Prestation', color: 'from-slate-500/10 to-slate-500/5', iconColor: 'text-slate-600', borderColor: 'border-slate-200' };
                const Icon = typeMeta.icon;
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => navigate(`/client/reserve/${w.id}`)}
                    data-testid={`widget-card-${w.id}`}
                    className={`text-left p-4 rounded-2xl border ${typeMeta.borderColor} bg-gradient-to-br ${typeMeta.color} hover:shadow-md transition-all hover:-translate-y-0.5 group`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-11 h-11 rounded-xl bg-white/80 flex items-center justify-center ${typeMeta.iconColor}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition" />
                    </div>
                    <p className="font-semibold text-slate-900 leading-tight">{w.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{typeMeta.label}</p>
                    {w.description && (
                      <p className="text-xs text-slate-600 mt-2 line-clamp-2">{w.description}</p>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {!activeSub && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-medium text-amber-900">Aucun abonnement actif</p>
                <p className="text-amber-700 mt-1">
                  Pour réserver des créneaux réguliers, contactez {company?.name || 'votre prestataire'} pour souscrire à un abonnement.
                </p>
                <div className="flex gap-3 mt-4">
                  {company?.phone && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`tel:${company.phone}`}>
                        <Phone className="w-4 h-4 mr-2" />
                        Appeler
                      </a>
                    </Button>
                  )}
                  {company?.email && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`mailto:${company.email}?subject=Demande d'abonnement`}>
                        <Mail className="w-4 h-4 mr-2" />
                        Envoyer un email
                      </a>
                    </Button>
                  )}
                  {(company?.widgets_by_type?.recurring || company?.widget_id) && (
                    <Button size="sm" onClick={() => {
                      const recWid = company?.widgets_by_type?.recurring;
                      if (recWid) window.open(`/widget/recurring/${recWid}`, '_blank');
                      else window.open(`/widget/punctual/${company.widget_id}`, '_blank');
                    }} data-testid="client-subscribe-widget-btn">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Souscrire un abonnement
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement...</div>
          ) : bookings.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Aucune réservation. Réservez votre premier créneau!
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Créneau</TableHead>
                  <TableHead>Heures</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.sort((a, b) => new Date(b.date) - new Date(a.date)).map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell className="font-medium">
                      {new Date(booking.date).toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long' 
                      })}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const info = getSlotTypeInfo(booking);
                        return (
                          <span className="inline-flex items-center gap-1.5">
                            <span>{slotTypeEmoji[info.kind]}</span>
                            <span>{info.timeRange || info.label}</span>
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{booking.hours_consumed}h</TableCell>
                    <TableCell>
                      <Badge className={
                        booking.status === 'confirmed' ? 'status-success' :
                        booking.status === 'completed' ? 'status-info' : 'status-error'
                      }>
                        {booking.status === 'confirmed' ? 'Confirmé' :
                         booking.status === 'completed' ? 'Terminé' : 'Annulé'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {booking.status === 'confirmed' && new Date(booking.date) > new Date() && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:bg-destructive/10"
                          data-testid={`cancel-booking-${booking.id}`}
                          onClick={() => handleCancel(booking.id)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Annuler
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Reserve a service through a company widget — embeds the public widget
// inside the client space, with the client's coordinates pre-filled.
function ReserveWidgetPage() {
  const { widgetId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeHeight, setIframeHeight] = useState(820); // sensible default

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, widgetsRes] = await Promise.all([
          clientApi.getProfile(),
          clientApi.getAvailableWidgets(),
        ]);
        setProfile(profileRes.data);
        const found = (widgetsRes.data || []).find((w) => w.id === widgetId);
        setWidget(found || null);
      } catch (e) {
        toast.error("Impossible de charger ce widget");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [widgetId]);

  // Listen for height updates posted by the embedded widget
  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data;
      if (data && data.type === 'resawork:widget-height' && typeof data.height === 'number') {
        // Clamp between 500 and 4000 to avoid runaway layouts
        const h = Math.max(500, Math.min(4000, Math.round(data.height)));
        setIframeHeight(h);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!widget) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/client/bookings')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Ce service n'est pas disponible dans votre espace client.
          </CardContent>
        </Card>
      </div>
    );
  }

  // Build the embed URL with prefill query params
  const params = new URLSearchParams({ embed: 'true' });
  if (profile?.name) params.set('prefill_name', profile.name);
  if (profile?.email) params.set('prefill_email', profile.email);
  if (profile?.phone) params.set('prefill_phone', profile.phone);
  if (profile?.address) params.set('prefill_address', profile.address);

  const widgetPath = {
    punctual: 'punctual',
    recurring: 'recurring',
    appointment: 'appointment',
  }[widget.widget_type] || 'punctual';

  const src = `/widget/${widgetPath}/${widget.id}?${params.toString()}`;

  return (
    <div className="space-y-4 -mx-4 sm:mx-0">
      <div className="flex items-center justify-between px-4 sm:px-0">
        <Button variant="ghost" size="sm" onClick={() => navigate('/client/bookings')} data-testid="reserve-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux réservations
        </Button>
        <div className="text-sm font-medium text-slate-700 hidden sm:block">
          {widget.name}
        </div>
      </div>
      <div className="bg-white sm:rounded-2xl sm:border sm:border-slate-200 overflow-hidden">
        <iframe
          title={widget.name}
          src={src}
          data-testid="reserve-widget-iframe"
          className="w-full border-0 block"
          style={{ height: `${iframeHeight}px`, transition: 'height 220ms ease' }}
        />
      </div>
    </div>
  );
}

// Credits History
// History Page (replacing Credits Page)
function HistoryPage() {
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookingsRes, invoicesRes] = await Promise.all([
          clientApi.getBookings(),
          clientApi.getInvoices()
        ]);
        setBookings(bookingsRes.data);
        setInvoices(invoicesRes.data);
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const completedBookings = bookings.filter(b => b.status === 'completed');
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');

  // Get invoice for a booking
  const getInvoiceForBooking = (bookingId) => {
    return invoices.find(inv => inv.booking_id === bookingId);
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-6">
      <div className="h-8 bg-muted rounded w-1/3" />
      <div className="h-48 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Historique & Factures</h1>
        <p className="text-muted-foreground mt-1">Vos interventions passées et factures</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{completedBookings.length}</p>
              <p className="text-xs text-muted-foreground">Interventions réalisées</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{invoices.length}</p>
              <p className="text-xs text-muted-foreground">Factures disponibles</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{cancelledBookings.length}</p>
              <p className="text-xs text-muted-foreground">Annulées</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Section */}
      {invoices.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Mes Factures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                  <div>
                    <p className="font-medium">
                      {invoice.booking?.date ? 
                        new Date(invoice.booking.date).toLocaleDateString('fr-FR', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        }) : 
                        'Prestation'
                      }
                    </p>
                    <p className="text-sm text-muted-foreground">{invoice.company_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {invoice.invoice_amount && (
                      <span className="font-semibold text-lg">{invoice.invoice_amount}€</span>
                    )}
                    <a 
                      href={invoice.invoice_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex"
                    >
                      <Button size="sm" className="gap-1">
                        <Download className="w-4 h-4" />
                        Télécharger
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Toutes les interventions</CardTitle>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucune intervention enregistrée</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.sort((a, b) => new Date(b.date) - new Date(a.date)).map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>
                      {new Date(booking.date).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const info = getSlotTypeInfo(booking);
                        return (
                          <Badge
                            variant="outline"
                            className={`gap-1.5 ${slotTypeBadgeClasses[info.kind]}`}
                            data-testid={`slot-badge-${booking.id}`}
                          >
                            <span>{slotTypeEmoji[info.kind]}</span>
                            <span>{info.label}</span>
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="font-medium">
                      {booking.price ? `${booking.price.toFixed(2)}€` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                        booking.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        ''
                      }>
                        {booking.status === 'completed' ? 'Terminé' :
                         booking.status === 'confirmed' ? 'Confirmé' :
                         booking.status === 'cancelled' ? 'Annulé' : booking.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Subscription Planning Page - View and manage subscription bookings
function SubscriptionPlanningPage() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // Reschedule dialog
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newPeriod, setNewPeriod] = useState('morning');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const response = await clientApi.getSubscriptions();
        setSubscriptions(response.data);
        if (response.data.length > 0) {
          setSelectedSubscription(response.data[0].id);
        }
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (selectedSubscription) {
      fetchSubscriptionDetails();
    }
  }, [selectedSubscription]);

  const fetchSubscriptionDetails = async () => {
    if (!selectedSubscription) return;
    setIsLoadingDetails(true);
    try {
      const response = await clientApi.getSubscriptionStats(selectedSubscription);
      setSubscriptionData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedBooking || !newDate) return;
    
    setIsSubmitting(true);
    try {
      await clientApi.rescheduleBooking(selectedBooking.id, newDate, newPeriod);
      toast.success('Demande de modification envoyée !');
      setIsRescheduleDialogOpen(false);
      setSelectedBooking(null);
      setNewDate('');
      fetchSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRescheduleDialog = (booking) => {
    if (!booking.can_modify) {
      toast.error(`Modification impossible : moins de 72H avant la date. Contactez l'entreprise.`);
      return;
    }
    setSelectedBooking(booking);
    setNewDate(booking.date);
    setNewPeriod(booking.slot_period || 'morning');
    setIsRescheduleDialogOpen(true);
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending_validation: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmé', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'En cours', color: 'bg-cyan-100 text-cyan-800' },
      completed: { label: 'Réalisé', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800' },
    };
    return configs[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Mon Abonnement</h1>
          <p className="text-muted-foreground mt-1">Gérez vos dates de passage</p>
        </div>
        <Card className="text-center py-12">
          <CardContent>
            <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Vous n'avez pas encore d'abonnement</p>
            <p className="text-muted-foreground mt-2">
              Demandez un abonnement via le widget de l'entreprise
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = subscriptionData?.stats || {};
  const bookings = subscriptionData?.bookings || [];
  const company = subscriptionData?.company || {};
  const progressPercent = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Mon Abonnement</h1>
        <p className="text-muted-foreground mt-1">Gérez vos dates de passage</p>
      </div>

      {/* Subscription Selector if multiple */}
      {subscriptions.length > 1 && (
        <Card>
          <CardContent className="p-4">
            <Select value={selectedSubscription} onValueChange={setSelectedSubscription}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionnez un abonnement" />
              </SelectTrigger>
              <SelectContent>
                {subscriptions.map(sub => (
                  <SelectItem key={sub.id} value={sub.id}>
                    Abonnement du {new Date(sub.created_at).toLocaleDateString('fr-FR')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {isLoadingDetails ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : subscriptionData ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-primary">
                  {stats.completed || 0} / {stats.total || 0}
                </div>
                <p className="text-sm text-muted-foreground mt-1">Passages réalisés</p>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{stats.confirmed || 0}</div>
                <p className="text-sm text-muted-foreground">À venir</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-yellow-600">{stats.pending || 0}</div>
                <p className="text-sm text-muted-foreground">En attente</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{stats.remaining || 0}</div>
                <p className="text-sm text-muted-foreground">Restants</p>
              </CardContent>
            </Card>
          </div>

          {/* Company Info */}
          {company && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Sprout className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{company.name}</p>
                    {company.phone && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {company.phone}
                      </p>
                    )}
                  </div>
                </div>
                <Badge>Votre prestataire</Badge>
              </CardContent>
            </Card>
          )}

          {/* Info about 72H restriction */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Modification des dates</p>
                <p className="text-sm text-amber-700">
                  Vous pouvez modifier vos dates de passage jusqu'à <strong>72 heures</strong> avant. 
                  Au-delà, contactez directement l'entreprise.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Bookings List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Planning des passages
              </CardTitle>
              <CardDescription>
                {stats.completed || 0} réalisés sur {stats.total || 0} programmés
              </CardDescription>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucun passage programmé
                </p>
              ) : (
                <div className="space-y-3">
                  {bookings.map(booking => {
                    const statusConfig = getStatusConfig(booking.status);
                    const bookingDate = new Date(booking.date);
                    const isPast = bookingDate < new Date();
                    
                    return (
                      <div 
                        key={booking.id}
                        className={`p-4 rounded-xl border ${
                          booking.status === 'completed' ? 'bg-green-50 border-green-200' :
                          booking.status === 'cancelled' ? 'bg-gray-50 border-gray-200 opacity-60' :
                          'bg-muted/30'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              booking.status === 'completed' ? 'bg-green-100' :
                              booking.status === 'in_progress' ? 'bg-cyan-100' :
                              'bg-primary/10'
                            }`}>
                              <CalendarDays className={`w-5 h-5 ${
                                booking.status === 'completed' ? 'text-green-600' :
                                booking.status === 'in_progress' ? 'text-cyan-600' :
                                'text-primary'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">
                                {bookingDate.toLocaleDateString('fr-FR', { 
                                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                                })}
                              </p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                                {(() => {
                                  const info = getSlotTypeInfo(booking);
                                  return (
                                    <>
                                      <span>{slotTypeEmoji[info.kind]}</span>
                                      <span>{info.label}</span>
                                      {info.timeRange && (
                                        <span className="text-xs opacity-70">({info.timeRange})</span>
                                      )}
                                    </>
                                  );
                                })()}
                              </p>
                              {!booking.can_modify && booking.status !== 'completed' && booking.status !== 'cancelled' && (
                                <p className="text-xs text-amber-600 mt-1">
                                  ⏰ Modification impossible (moins de 72H)
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                            
                            {booking.can_modify && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => openRescheduleDialog(booking)}
                              >
                                <Calendar className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Photos if completed */}
                        {booking.status === 'completed' && booking.photos?.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-sm font-medium mb-2">Photos de l'intervention</p>
                            <div className="grid grid-cols-2 gap-2">
                              {booking.photos.map(photo => (
                                <div key={photo.id} className="relative">
                                  <img 
                                    src={`${getAssetUrl(photo.url)}`}
                                    alt={photo.photo_type === 'before' ? 'Avant' : 'Après'}
                                    className="w-full h-24 object-cover rounded-lg"
                                  />
                                  <Badge 
                                    className={`absolute top-1 left-1 text-xs ${
                                      photo.photo_type === 'before' 
                                        ? 'bg-blue-100 text-blue-700' 
                                        : 'bg-green-100 text-green-700'
                                    }`}
                                  >
                                    {photo.photo_type === 'before' ? 'Avant' : 'Après'}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la date</DialogTitle>
            <DialogDescription>
              Choisissez une nouvelle date pour ce passage
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedBooking && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Date actuelle</p>
                <p className="font-medium">
                  {new Date(selectedBooking.date).toLocaleDateString('fr-FR', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Nouvelle date *</label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Minimum 72H à partir d'aujourd'hui
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Période</label>
              <Select value={newPeriod} onValueChange={setNewPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">Matin (8h-12h)</SelectItem>
                  <SelectItem value="afternoon">Après-midi (14h-18h)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Note :</strong> Votre demande sera envoyée à l'entreprise pour validation.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setIsRescheduleDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleReschedule}
                disabled={!newDate || isSubmitting}
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer la demande'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Reviews Page - Donner son avis après une prestation
function ReviewsPage() {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [submittedReviews, setSubmittedReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [pendingRes, reviewsRes] = await Promise.all([
        clientApi.getPendingReviews(),
        clientApi.getReviews()
      ]);
      setPendingReviews(pendingRes.data);
      setSubmittedReviews(reviewsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error('Veuillez donner une note');
      return;
    }

    setIsSubmitting(true);
    try {
      await clientApi.submitReview({
        intervention_id: selectedIntervention.id,
        rating,
        comment: comment || null,
        collaborator_id: selectedIntervention.booking?.collaborator_id || null
      });
      toast.success('Merci pour votre avis !');
      setIsDialogOpen(false);
      setRating(0);
      setComment('');
      setSelectedIntervention(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange, readOnly = false, size = 'md' }) => {
    const sizeClass = size === 'lg' ? 'w-8 h-8' : size === 'sm' ? 'w-4 h-4' : 'w-6 h-6';
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange?.(star)}
            className={`${readOnly ? '' : 'cursor-pointer hover:scale-110'} transition-transform`}
          >
            <Star
              className={`${sizeClass} ${
                star <= value 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-6">
      <div className="h-8 bg-muted rounded w-1/3" />
      <div className="h-48 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Mes Avis</h1>
        <p className="text-muted-foreground mt-1">Évaluez vos prestations</p>
      </div>

      {/* Pending Reviews Banner */}
      {pendingReviews.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-yellow-800">
                  {pendingReviews.length} prestation(s) à évaluer
                </p>
                <p className="text-sm text-yellow-700">
                  Votre avis nous aide à améliorer nos services !
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Reviews */}
      {pendingReviews.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Prestations à évaluer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingReviews.map((intervention) => (
              <div 
                key={intervention.id} 
                className="flex items-center justify-between p-4 bg-muted/50 rounded-xl"
              >
                <div>
                  <p className="font-medium">
                    {intervention.booking?.date ? 
                      new Date(intervention.booking.date).toLocaleDateString('fr-FR', {
                        weekday: 'long', day: 'numeric', month: 'long'
                      }) : 'Prestation terminée'
                    }
                  </p>
                  {intervention.collaborator && (
                    <p className="text-sm text-muted-foreground">
                      Intervenant : {intervention.collaborator.name}
                    </p>
                  )}
                </div>
                <Dialog open={isDialogOpen && selectedIntervention?.id === intervention.id} onOpenChange={(open) => {
                  setIsDialogOpen(open);
                  if (!open) {
                    setRating(0);
                    setComment('');
                    setSelectedIntervention(null);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="gap-1"
                      data-testid={`review-btn-${intervention.id}`}
                      onClick={() => setSelectedIntervention(intervention)}
                    >
                      <Star className="w-4 h-4" />
                      Donner mon avis
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading">Évaluer la prestation</DialogTitle>
                      <DialogDescription>
                        {intervention.booking?.date ? 
                          `Prestation du ${new Date(intervention.booking.date).toLocaleDateString('fr-FR', {
                            weekday: 'long', day: 'numeric', month: 'long'
                          })}` : 'Votre avis compte pour nous'
                        }
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 mt-4">
                      <div className="text-center space-y-2">
                        <p className="text-sm text-muted-foreground">Votre note</p>
                        <div className="flex justify-center">
                          <StarRating value={rating} onChange={setRating} size="lg" />
                        </div>
                        <p className="text-sm font-medium">
                          {rating === 1 && 'Très insatisfait'}
                          {rating === 2 && 'Insatisfait'}
                          {rating === 3 && 'Correct'}
                          {rating === 4 && 'Satisfait'}
                          {rating === 5 && 'Très satisfait'}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Commentaire (optionnel)</label>
                        <Textarea
                          placeholder="Partagez votre expérience..."
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          rows={4}
                          data-testid="review-comment"
                        />
                      </div>

                      <Button 
                        className="w-full rounded-full gap-2" 
                        onClick={handleSubmitReview}
                        disabled={isSubmitting || rating === 0}
                        data-testid="submit-review-btn"
                      >
                        <Send className="w-4 h-4" />
                        {isSubmitting ? 'Envoi...' : 'Envoyer mon avis'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Submitted Reviews */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Mes avis déposés</CardTitle>
          <CardDescription>Historique de vos évaluations</CardDescription>
        </CardHeader>
        <CardContent>
          {submittedReviews.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Star className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun avis déposé pour le moment</p>
            </div>
          ) : (
            <div className="space-y-4">
              {submittedReviews.map((review) => (
                <div key={review.id} className="p-4 border rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <StarRating value={review.rating} readOnly size="sm" />
                      <p className="text-sm text-muted-foreground mt-1">
                        {new Date(review.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                        {review.collaborator_name && ` • ${review.collaborator_name}`}
                      </p>
                    </div>
                    <Badge variant="secondary">Envoyé</Badge>
                  </div>
                  {review.comment && (
                    <p className="text-sm mt-3 text-muted-foreground italic">
                      "{review.comment}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Intervention Validation Page - Client validates completed interventions
function InterventionValidationPage() {
  const [pendingValidations, setPendingValidations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDisputing, setIsDisputing] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const response = await clientApi.getPendingValidations();
      setPendingValidations(response.data || []);
    } catch (error) {
      console.error('Error fetching pending validations:', error);
      // If 404, likely means endpoint doesn't exist yet or no data
      setPendingValidations([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleValidate = async (interventionId) => {
    setIsSubmitting(true);
    try {
      await clientApi.validateIntervention(interventionId);
      toast.success('Intervention validée avec succès !');
      setIsDialogOpen(false);
      setSelectedIntervention(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDispute = async (interventionId) => {
    if (!disputeReason.trim()) {
      toast.error('Veuillez indiquer le motif de votre contestation');
      return;
    }
    setIsSubmitting(true);
    try {
      await clientApi.disputeIntervention(interventionId, disputeReason);
      toast.success('Contestation envoyée à l\'entreprise');
      setIsDialogOpen(false);
      setSelectedIntervention(null);
      setDisputeReason('');
      setIsDisputing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la contestation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openInterventionDialog = (intervention) => {
    setSelectedIntervention(intervention);
    setIsDialogOpen(true);
    setIsDisputing(false);
    setDisputeReason('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Validation des interventions</h1>
        <p className="text-muted-foreground mt-1">Validez les prestations réalisées</p>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Comment ça marche ?</p>
            <p className="text-sm text-blue-700">
              Après chaque intervention, vous pouvez consulter les photos avant/après et 
              <strong> valider la prestation</strong>. Si vous constatez un problème, 
              vous pouvez <strong>contester</strong> en indiquant le motif.
            </p>
          </div>
        </CardContent>
      </Card>

      {pendingValidations.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
            <p className="text-lg font-medium">Aucune intervention en attente</p>
            <p className="text-muted-foreground mt-2">
              Toutes vos interventions ont été validées.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingValidations.map((intervention) => {
            const bookingDate = intervention.booking?.date 
              ? new Date(intervention.booking.date).toLocaleDateString('fr-FR', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })
              : 'Date inconnue';
            
            const beforePhoto = intervention.intervention_photos?.find(p => p.photo_type === 'before');
            const afterPhoto = intervention.intervention_photos?.find(p => p.photo_type === 'after');
            
            return (
              <Card key={intervention.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Photos Preview */}
                    <div className="w-full md:w-1/3 bg-muted/30 p-4">
                      <div className="grid grid-cols-2 gap-2">
                        {beforePhoto ? (
                          <div className="relative">
                            <img 
                              src={`${getAssetUrl(beforePhoto.url)}`}
                              alt="Avant"
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <Badge className="absolute top-1 left-1 text-xs bg-blue-100 text-blue-700">
                              Avant
                            </Badge>
                          </div>
                        ) : (
                          <div className="w-full h-24 bg-muted rounded-lg flex items-center justify-center">
                            <span className="text-xs text-muted-foreground">Pas de photo</span>
                          </div>
                        )}
                        {afterPhoto ? (
                          <div className="relative">
                            <img 
                              src={`${getAssetUrl(afterPhoto.url)}`}
                              alt="Après"
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <Badge className="absolute top-1 left-1 text-xs bg-green-100 text-green-700">
                              Après
                            </Badge>
                          </div>
                        ) : (
                          <div className="w-full h-24 bg-muted rounded-lg flex items-center justify-center">
                            <span className="text-xs text-muted-foreground">Pas de photo</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 p-4 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold capitalize">{bookingDate}</p>
                          <Badge className="bg-amber-100 text-amber-700">En attente de validation</Badge>
                        </div>
                        {intervention.collaborator?.name && (
                          <p className="text-sm text-muted-foreground">
                            Intervenant : {intervention.collaborator.name}
                          </p>
                        )}
                        {intervention.completed_at && (
                          <p className="text-sm text-muted-foreground">
                            Terminée le {new Date(intervention.completed_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                        <Button 
                          className="flex-1 rounded-full"
                          onClick={() => openInterventionDialog(intervention)}
                          data-testid={`view-intervention-${intervention.id}`}
                        >
                          Voir les détails
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Intervention Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          setSelectedIntervention(null);
          setIsDisputing(false);
          setDisputeReason('');
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Détails de l'intervention</DialogTitle>
            <DialogDescription>
              {selectedIntervention?.booking?.date && (
                <>Intervention du {new Date(selectedIntervention.booking.date).toLocaleDateString('fr-FR', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedIntervention && (
            <div className="space-y-6 py-4">
              {/* Photos Section */}
              <div>
                <h3 className="text-sm font-medium mb-3">Photos de l'intervention</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Before Photo */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Avant</Badge>
                    </p>
                    {selectedIntervention.intervention_photos?.find(p => p.photo_type === 'before') ? (
                      <img 
                        src={`${getAssetUrl(selectedIntervention.intervention_photos.find(p => p.photo_type === 'before').url)}`}
                        alt="Avant l'intervention"
                        className="w-full h-48 object-cover rounded-xl border"
                      />
                    ) : (
                      <div className="w-full h-48 bg-muted rounded-xl flex items-center justify-center">
                        <p className="text-sm text-muted-foreground">Aucune photo</p>
                      </div>
                    )}
                  </div>
                  
                  {/* After Photo */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <Badge className="bg-green-100 text-green-700 text-xs">Après</Badge>
                    </p>
                    {selectedIntervention.intervention_photos?.find(p => p.photo_type === 'after') ? (
                      <img 
                        src={`${getAssetUrl(selectedIntervention.intervention_photos.find(p => p.photo_type === 'after').url)}`}
                        alt="Après l'intervention"
                        className="w-full h-48 object-cover rounded-xl border"
                      />
                    ) : (
                      <div className="w-full h-48 bg-muted rounded-xl flex items-center justify-center">
                        <p className="text-sm text-muted-foreground">Aucune photo</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Intervention Info */}
              <div className="p-4 bg-muted/50 rounded-xl space-y-2">
                {selectedIntervention.collaborator?.name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Intervenant</span>
                    <span className="font-medium">{selectedIntervention.collaborator.name}</span>
                  </div>
                )}
                {selectedIntervention.started_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Démarrée à</span>
                    <span className="font-medium">
                      {new Date(selectedIntervention.started_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {selectedIntervention.completed_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Terminée à</span>
                    <span className="font-medium">
                      {new Date(selectedIntervention.completed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
              </div>

              {/* Dispute Form */}
              {isDisputing ? (
                <div className="space-y-4">
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">
                      <strong>Attention :</strong> Une contestation sera envoyée à l'entreprise. 
                      Veuillez détailler le motif de votre mécontentement.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Motif de la contestation *</label>
                    <Textarea
                      placeholder="Expliquez le problème constaté..."
                      value={disputeReason}
                      onChange={(e) => setDisputeReason(e.target.value)}
                      rows={4}
                      data-testid="dispute-reason"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setIsDisputing(false);
                        setDisputeReason('');
                      }}
                    >
                      Annuler
                    </Button>
                    <Button 
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleDispute(selectedIntervention.id)}
                      disabled={isSubmitting || !disputeReason.trim()}
                      data-testid="confirm-dispute-btn"
                    >
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer la contestation'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsDisputing(true)}
                    data-testid="dispute-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Contester
                  </Button>
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => handleValidate(selectedIntervention.id)}
                    disabled={isSubmitting}
                    data-testid="validate-intervention-btn"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Valider l'intervention
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



// Support/Tickets Page
function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'bug'
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await clientApi.getTickets();
      setTickets(response.data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newTicket.title || !newTicket.description) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setIsSubmitting(true);
    try {
      await clientApi.createTicket(newTicket);
      toast.success('Ticket créé avec succès');
      setIsDialogOpen(false);
      setNewTicket({ title: '', description: '', category: 'bug' });
      fetchTickets();
    } catch (error) {
      toast.error('Erreur lors de la création du ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open': return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" />Ouvert</Badge>;
      case 'in_progress': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Clock className="w-3 h-3 mr-1" />En cours</Badge>;
      case 'resolved': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" />Résolu</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'bug': return 'Bug';
      case 'feature': return 'Suggestion';
      case 'question': return 'Question';
      default: return category;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Support</h1>
          <p className="text-muted-foreground">Signalez un problème ou posez une question</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full">
              <Send className="w-4 h-4 mr-2" />
              Nouveau ticket
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un ticket</DialogTitle>
              <DialogDescription>
                Décrivez votre problème ou suggestion
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Catégorie</label>
                <Select value={newTicket.category} onValueChange={(v) => setNewTicket({...newTicket, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">🐛 Signaler un bug</SelectItem>
                    <SelectItem value="feature">💡 Suggestion</SelectItem>
                    <SelectItem value="question">❓ Question</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Titre</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-lg"
                  placeholder="Résumé du problème"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  placeholder="Décrivez le problème en détail..."
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  rows={4}
                />
              </div>
              <Button className="w-full" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">Aucun ticket</h3>
            <p className="text-muted-foreground">Vous n'avez pas encore créé de ticket de support</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <Card key={ticket.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{getCategoryLabel(ticket.category)}</Badge>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <h3 className="font-semibold">{ticket.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ticket.description}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Créé le {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                {ticket.admin_response && (
                  <div className="mt-4 p-3 bg-primary/5 rounded-lg border-l-2 border-primary">
                    <p className="text-xs text-muted-foreground mb-1">Réponse du support :</p>
                    <p className="text-sm">{ticket.admin_response}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Scheduling Page - Flexible planning for subscription slots
function SchedulingPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSub, setSelectedSub] = useState(null);
  const [schedulingInfo, setSchedulingInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isScheduling, setIsScheduling] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('morning');

  // Fetch subscriptions on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const subsRes = await clientApi.getSubscriptions();
        const activeSubs = subsRes.data.filter(s => s.is_active);
        setSubscriptions(activeSubs);
        if (activeSubs.length > 0) {
          setSelectedSub(activeSubs[0]);
        }
      } catch (error) {
        toast.error('Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch scheduling info when subscription changes
  useEffect(() => {
    if (!selectedSub) return;
    
    const fetchSchedulingInfo = async () => {
      try {
        const res = await clientApi.getSubscriptionSchedulingInfo(selectedSub.id);
        setSchedulingInfo(res.data);
      } catch (error) {
        console.error('Error fetching scheduling info:', error);
        toast.error('Erreur lors du chargement des informations');
      }
    };
    fetchSchedulingInfo();
  }, [selectedSub]);

  // Calendar helpers
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the Monday of the first week
    const startDate = new Date(firstDay);
    const dayOfWeek = startDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + diff);
    
    // Get all days until Sunday of the last week
    const endDate = new Date(lastDay);
    const endDayOfWeek = endDate.getDay();
    const endDiff = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
    endDate.setDate(endDate.getDate() + endDiff);
    
    const days = [];
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const isDateBooked = (date) => {
    if (!schedulingInfo?.booked_slots) return false;
    const dateStr = date.toISOString().split('T')[0];
    return schedulingInfo.booked_slots.some(slot => slot.date === dateStr);
  };

  const isDateScheduledByMe = (date) => {
    if (!schedulingInfo?.scheduled_bookings) return false;
    const dateStr = date.toISOString().split('T')[0];
    return schedulingInfo.scheduled_bookings.some(b => b.date === dateStr);
  };

  const isWorkingDay = (date) => {
    if (!schedulingInfo?.work_hours || schedulingInfo.work_hours.length === 0) return true;
    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1; // Convert to Mon=0
    return schedulingInfo.work_hours.some(wh => wh.day_of_week === dayIndex && wh.is_active);
  };

  const handleScheduleSlot = async () => {
    if (!selectedDate || !selectedSub) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    setIsScheduling(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await clientApi.scheduleSubscriptionSlot(selectedSub.id, {
        date: dateStr,
        period: selectedPeriod
      });
      toast.success('Créneau planifié avec succès !');
      setSelectedDate(null);
      
      // Refresh scheduling info
      const res = await clientApi.getSubscriptionSchedulingInfo(selectedSub.id);
      setSchedulingInfo(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la planification');
    } finally {
      setIsScheduling(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const getFrequencyLabel = (freq) => {
    switch (freq) {
      case 'weekly': return 'Hebdomadaire';
      case 'biweekly': return 'Bi-mensuel';
      case 'monthly': return 'Mensuel';
      default: return freq;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Planifier mes créneaux</h1>
          <p className="text-muted-foreground mt-1">Planifiez vos interventions selon vos disponibilités</p>
        </div>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6 text-center">
            <CalendarPlus className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="font-semibold text-amber-900 mb-2">Aucun abonnement actif</h3>
            <p className="text-amber-700">
              Vous n'avez pas d'abonnement actif. Contactez votre prestataire pour souscrire à un abonnement.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const remainingSlots = schedulingInfo?.subscription?.remaining_slots || 0;
  const totalSlots = schedulingInfo?.subscription?.total_expected_slots || 0;
  const scheduledSlots = schedulingInfo?.subscription?.scheduled_slots || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Planifier mes créneaux</h1>
        <p className="text-muted-foreground mt-1">Planifiez vos interventions selon vos disponibilités</p>
      </div>

      {/* Subscription selector if multiple */}
      {subscriptions.length > 1 && (
        <Card>
          <CardContent className="p-4">
            <label className="text-sm font-medium mb-2 block">Abonnement</label>
            <Select value={selectedSub?.id} onValueChange={(id) => setSelectedSub(subscriptions.find(s => s.id === id))}>
              <SelectTrigger data-testid="subscription-select">
                <SelectValue placeholder="Sélectionnez un abonnement" />
              </SelectTrigger>
              <SelectContent>
                {subscriptions.map(sub => (
                  <SelectItem key={sub.id} value={sub.id}>
                    {getFrequencyLabel(sub.frequency)} - {sub.slot_type === 'full_day' ? 'Journée' : 'Demi-journée'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Progress card */}
      <Card className="border-primary/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">Progression de votre abonnement</h3>
              <p className="text-sm text-muted-foreground">
                {getFrequencyLabel(schedulingInfo?.subscription?.frequency)} • {schedulingInfo?.subscription?.slot_type === 'full_day' ? 'Journée complète' : 'Demi-journée'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-primary">{scheduledSlots} / {totalSlots}</p>
              <p className="text-sm text-muted-foreground">créneaux planifiés</p>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-500 rounded-full"
              style={{ width: `${totalSlots > 0 ? (scheduledSlots / totalSlots) * 100 : 0}%` }}
            />
          </div>
          
          {remainingSlots > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-700">
                  <strong>{remainingSlots} créneau(x)</strong> restant(s) à planifier. 
                  Sélectionnez une date dans le calendrier ci-dessous.
                </p>
              </div>
            </div>
          )}
          
          {remainingSlots === 0 && totalSlots > 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <p className="text-sm text-green-700">
                  Tous vos créneaux sont planifiés !
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calendar and scheduling */}
      {remainingSlots > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <CardTitle className="text-lg capitalize">
                  {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                  <div key={day} className="text-xs font-medium text-muted-foreground py-2">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth().map((day, idx) => {
                  const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                  const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
                  const isBooked = isDateBooked(day);
                  const isScheduledByMe = isDateScheduledByMe(day);
                  const isWorking = isWorkingDay(day);
                  const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                  const isDisabled = !isCurrentMonth || isPast || isBooked || !isWorking;

                  return (
                    <button
                      key={idx}
                      disabled={isDisabled}
                      onClick={() => !isDisabled && setSelectedDate(day)}
                      className={`
                        aspect-square p-1 rounded-lg text-sm font-medium transition-all
                        ${!isCurrentMonth ? 'opacity-30 text-muted-foreground' : ''}
                        ${isPast ? 'opacity-50 cursor-not-allowed' : ''}
                        ${isBooked && !isScheduledByMe ? 'bg-red-100 text-red-500 cursor-not-allowed' : ''}
                        ${isScheduledByMe ? 'bg-green-100 text-green-600' : ''}
                        ${!isWorking && isCurrentMonth && !isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
                        ${isSelected ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2' : ''}
                        ${!isDisabled && !isSelected && !isScheduledByMe ? 'hover:bg-primary/10 cursor-pointer' : ''}
                      `}
                      data-testid={`calendar-day-${day.getDate()}`}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
                  <span className="text-muted-foreground">Mes créneaux</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-red-100 border border-red-300" />
                  <span className="text-muted-foreground">Indisponible</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
                  <span className="text-muted-foreground">Fermé</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selection panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Planifier un créneau</CardTitle>
              <CardDescription>
                Sélectionnez une date et confirmez votre réservation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedDate ? (
                <>
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-sm text-muted-foreground">Date sélectionnée</p>
                    <p className="text-lg font-semibold capitalize">{formatDate(selectedDate)}</p>
                  </div>
                  
                  {schedulingInfo?.subscription?.slot_type === 'half_day' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Créneau horaire</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setSelectedPeriod('morning')}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            selectedPeriod === 'morning' 
                              ? 'border-primary bg-primary/10 text-primary' 
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <span className="text-lg">🌅</span>
                          <p className="text-sm font-medium mt-1">Matin</p>
                        </button>
                        <button
                          onClick={() => setSelectedPeriod('afternoon')}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            selectedPeriod === 'afternoon' 
                              ? 'border-primary bg-primary/10 text-primary' 
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <span className="text-lg">☀️</span>
                          <p className="text-sm font-medium mt-1">Après-midi</p>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type d'intervention</span>
                      <span className="font-medium">
                        {schedulingInfo?.subscription?.slot_type === 'full_day' ? 'Journée complète' : 'Demi-journée'}
                      </span>
                    </div>
                    {schedulingInfo?.subscription?.services?.length > 0 && (
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-muted-foreground">Services</span>
                        <span className="font-medium text-right max-w-[60%]">
                          {schedulingInfo.subscription.services.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full rounded-full" 
                    size="lg"
                    onClick={handleScheduleSlot}
                    disabled={isScheduling}
                    data-testid="confirm-schedule-btn"
                  >
                    {isScheduling ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Planification...
                      </>
                    ) : (
                      <>
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        Confirmer ce créneau
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">
                    Sélectionnez une date dans le calendrier pour planifier votre prochain créneau.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scheduled bookings list */}
      {schedulingInfo?.scheduled_bookings?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              Vos créneaux planifiés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {schedulingInfo.scheduled_bookings
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((booking) => (
                  <div 
                    key={booking.id} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium capitalize">
                        {new Date(booking.date).toLocaleDateString('fr-FR', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long' 
                        })}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        {(() => {
                          const info = getSlotTypeInfo(booking);
                          return (
                            <>
                              <span>{slotTypeEmoji[info.kind]}</span>
                              <span>{info.label}</span>
                              {info.timeRange && (
                                <span className="text-xs opacity-70">· {info.timeRange}</span>
                              )}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                    <Badge className={
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }>
                      {booking.status === 'confirmed' ? 'Confirmé' : 
                       booking.status === 'completed' ? 'Terminé' : booking.status}
                    </Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Main Client Dashboard Layout
export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [accountStatus, setAccountStatus] = useState(user?.account_status || 'approved');

  // Helper to build full logo URL — uses smart prod/preview detection
  const getLogoUrl = getAssetUrl;

  // Fetch company info (logo) on mount
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const res = await clientApi.getProfile();
        if (res.data?.company) {
          setCompanyInfo(res.data.company);
        }
        // Update account status from profile
        if (res.data?.account_status) {
          setAccountStatus(res.data.account_status);
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompanyInfo();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Déconnexion réussie');
  };

  const navItems = [
    { path: '/client', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { path: '/client/subscription', icon: CalendarDays, label: 'Mon Abonnement' },
    { path: '/client/validations', icon: CheckCircle2, label: 'Validations' },
    { path: '/client/schedule', icon: CalendarPlus, label: 'Planifier' },
    { path: '/client/bookings', icon: Calendar, label: 'Réservations' },
    { path: '/client/history', icon: FileText, label: 'Historique' },
    { path: '/client/support', icon: MessageSquare, label: 'Support' },
  ];

  // Pending account banner component
  const PendingAccountBanner = () => (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Clock className="w-5 h-5 text-amber-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-amber-800">
            Compte en attente de validation
          </h3>
          <p className="text-sm text-amber-700 mt-1">
            Votre compte est en cours de vérification par {companyInfo?.name || "l'entreprise"}. 
            Vous recevrez un email dès que votre compte sera validé. En attendant, vous pouvez 
            consulter les informations disponibles mais certaines fonctionnalités sont limitées.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <header className="lg:hidden flex items-center justify-between p-4 bg-card border-b border-border sticky top-0 z-50">
        <NavLink to="/client" end className="flex items-center gap-3 hover:opacity-80 transition-opacity" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-logo-home-link">
          {companyInfo?.logo_url ? (
            <img src={getLogoUrl(companyInfo.logo_url)} alt="" className="w-8 h-8 object-cover rounded-full border bg-white" loading="lazy" decoding="async" />
          ) : (
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Sprout className="w-4 h-4 text-primary-foreground" />
            </div>
          )}
          <span className="font-heading font-semibold text-sm">{companyInfo?.name || 'Mon Espace'}</span>
        </NavLink>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[57px] bg-background/95 backdrop-blur-sm z-40 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
            <div className="pt-4 mt-4 border-t border-border">
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium">{user?.name?.charAt(0) || 'C'}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{user?.name || 'Client'}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-destructive px-4"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-card border-r border-border p-6 flex-col">
        <NavLink to="/client" end className="flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity" data-testid="sidebar-logo-home-link">
          {companyInfo?.logo_url ? (
            <img 
              src={getLogoUrl(companyInfo.logo_url)} 
              alt={companyInfo.name || 'Logo'} 
              className="w-10 h-10 object-cover rounded-full border bg-white"
              data-testid="company-logo" loading="lazy" decoding="async"
            />
          ) : (
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Sprout className="w-5 h-5 text-primary-foreground" />
            </div>
          )}
          <div>
            <h2 className="font-heading font-semibold">{companyInfo?.name || 'Mon Espace'}</h2>
            <p className="text-xs text-muted-foreground">Espace Client</p>
          </div>
        </NavLink>

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              data-testid={`client-nav-${item.path.replace('/client', '').replace('/', '') || 'dashboard'}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="pt-6 border-t border-border">
          <ClientDashboardGuideTour buttonClassName="w-full flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground px-4 py-2 mb-2 rounded-lg hover:bg-muted transition-colors" />
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
              <span className="text-sm font-medium">{user?.name?.charAt(0) || 'C'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Client'}</p>
              <p className="text-xs text-muted-foreground">Client</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            data-testid="logout-btn"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:p-0 overflow-auto">
        {/* Desktop top bar with the notification bell aligned to the right */}
        <div className="hidden lg:flex sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-border px-8 py-3 items-center justify-end gap-3">
          <NotificationBell />
        </div>
        <div className="p-4 lg:p-8">
          {accountStatus === 'pending' && <PendingAccountBanner />}
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="subscription" element={<SubscriptionPlanningPage />} />
            <Route path="validations" element={<InterventionValidationPage />} />
            <Route path="schedule" element={<SchedulingPage />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="reserve/:widgetId" element={<ReserveWidgetPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
          </Routes>
        </div>
      </main>
      {/* In-app new version notification */}
      <NewVersionToast />
    </div>
  );
}
